import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, IconButton, Chip, useTheme, Tooltip,
  Button, TextField, Checkbox,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  AutoAwesome as PlaybookIcon,
  Lightbulb as LightbulbIcon,
  CheckCircle as CorrectIcon,
  Cancel as IncorrectIcon,
  Adjust as PartialIcon,
  HowToVote as VerdictIcon,
  Block as SkippedIcon,
  RadioButtonUnchecked as UnreviewedIcon,
  Notes as NotesIcon,
  History as HistoryIcon,
  EmojiEvents as GoldenSetIcon,
} from '@mui/icons-material';
import { RootState, IncidentMemory, GoldenSetEntry } from '../types';
import { fetchRecommendationsForSession, clearRecommendations } from '../store/slices/playbooksSlice';
import { PlaybooksService } from '../services/playbooksService';
import { CuratorService } from '../services/curatorService';
import { fetchClients } from '../store/slices/clientsSlice';
import RecommendationCard from '../components/playbook/RecommendationCard';
import DDoSPlaybookPanel from '../components/DDoSPlaybookPanel';
import { getRelativeTimeString } from '../utils/dateUtils';
import { colorUtils } from '../theme';

const SessionDetailPage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { recommendations, selectedSession, isDetailsLoading, error } = useSelector(
    (state: RootState) => state.playbooks,
  );
  const { clients } = useSelector((state: RootState) => state.clients);

  const [verdictSelected, setVerdictSelected] = useState<'correct' | 'incorrect' | 'partial' | ''>('');
  const [verdictNotes, setVerdictNotes] = useState('');
  const [verdictSubmitting, setVerdictSubmitting] = useState(false);
  const [verdictSaved, setVerdictSaved] = useState(false);
  const [verdictError, setVerdictError] = useState<string | null>(null);

  // Curation state — senior/curator editing surface
  const [editedSteps, setEditedSteps] = useState<string[]>([]);
  const [pastIncidents, setPastIncidents] = useState<IncidentMemory[]>([]);
  const [pastIncidentsLoading, setPastIncidentsLoading] = useState(false);
  const [selectedChunkIds, setSelectedChunkIds] = useState<Set<string>>(new Set());
  const [goldenEntry, setGoldenEntry] = useState<GoldenSetEntry | null>(null);
  const [goldenEntryLoading, setGoldenEntryLoading] = useState(false);
  const [goldenNotes, setGoldenNotes] = useState('');
  const [curating, setCurating] = useState(false);
  const [curateError, setCurateError] = useState<string | null>(null);
  const [curateSuccess, setCurateSuccess] = useState(false);

  useEffect(() => {
    if (!id) return;
    dispatch(fetchRecommendationsForSession(id) as any);
    if (clients.length === 0) dispatch(fetchClients() as any);
    return () => { dispatch(clearRecommendations()); };
  }, [id]);

  useEffect(() => {
    if (selectedSession?.analyst_verdict) {
      setVerdictSelected(selectedSession.analyst_verdict);
      setVerdictNotes(selectedSession.analyst_verdict_notes ?? '');
      setVerdictSaved(true);
    }
  }, [selectedSession?.analyst_verdict]);

  // Seed editable step text whenever the session (re)loads
  useEffect(() => {
    setEditedSteps((selectedSession?.ai_next_steps ?? []).map((s) => s.step));
  }, [selectedSession?.id, selectedSession?.ai_next_steps]);

  // Fetch full past-incident cards for the retrieved context chunks
  useEffect(() => {
    const refs = selectedSession?.retrieved_incident_ids;
    if (!refs?.length || !selectedSession) { setPastIncidents([]); return; }
    setPastIncidentsLoading(true);
    CuratorService.getMemories(refs.map((r) => r.id), selectedSession.client_id)
      .then(setPastIncidents)
      .catch(() => setPastIncidents([]))
      .finally(() => setPastIncidentsLoading(false));
  }, [selectedSession?.id, selectedSession?.retrieved_incident_ids]);

  // Look up whether this session already has a golden-set entry
  useEffect(() => {
    if (!selectedSession) { setGoldenEntry(null); return; }
    setGoldenEntryLoading(true);
    PlaybooksService.getGoldenSetEntries(selectedSession.client_id, { session_id: selectedSession.id })
      .then((res) => {
        const entry = res.entries[0] ?? null;
        setGoldenEntry(entry);
        setGoldenNotes(entry?.notes ?? '');
        setSelectedChunkIds(new Set(entry?.relevant_chunk_ids ?? []));
      })
      .catch(() => setGoldenEntry(null))
      .finally(() => setGoldenEntryLoading(false));
  }, [selectedSession?.id]);

  const handleSubmitVerdict = async () => {
    if (!id || !verdictSelected) return;
    setVerdictSubmitting(true);
    setVerdictError(null);
    try {
      await PlaybooksService.setSessionVerdict(id, verdictSelected, verdictNotes || undefined);
      setVerdictSaved(true);
    } catch (e: any) {
      setVerdictError(e.message);
    } finally {
      setVerdictSubmitting(false);
    }
  };

  const toggleChunk = (memoryId: string) => {
    setSelectedChunkIds((prev) => {
      const next = new Set(prev);
      if (next.has(memoryId)) next.delete(memoryId); else next.add(memoryId);
      return next;
    });
  };

  const buildBaselineNextSteps = () => {
    const original = selectedSession?.ai_next_steps ?? [];
    return editedSteps.map((step, idx) => ({ step, rationale: original[idx]?.rationale ?? '' }));
  };

  const handleAddToGoldenSet = async () => {
    if (!selectedSession) return;
    setCurating(true);
    setCurateError(null);
    try {
      const entry = await PlaybooksService.createGoldenSetEntry({
        client_id: selectedSession.client_id,
        session_id: selectedSession.id,
        notes: goldenNotes || undefined,
        baseline_next_steps: buildBaselineNextSteps(),
        relevant_chunk_ids: Array.from(selectedChunkIds),
      });
      setGoldenEntry(entry);
      setCurateSuccess(true);
      setTimeout(() => setCurateSuccess(false), 3000);
    } catch (e: any) {
      setCurateError(e.message);
    } finally {
      setCurating(false);
    }
  };

  const handleCurate = async () => {
    if (!selectedSession || !goldenEntry) return;
    setCurating(true);
    setCurateError(null);
    try {
      const newNextSteps = buildBaselineNextSteps();
      const stepsChanged = JSON.stringify(newNextSteps.map((s) => s.step))
        !== JSON.stringify((goldenEntry.baseline_next_steps ?? []).map((s) => s.step));

      const originalCStar = new Set(goldenEntry.relevant_chunk_ids ?? []);
      const toAdd = Array.from(selectedChunkIds).filter((cid) => !originalCStar.has(cid));
      const toRemove = Array.from(originalCStar).filter((cid) => !selectedChunkIds.has(cid));

      const calls: Promise<any>[] = [];
      if (stepsChanged) {
        calls.push(CuratorService.updateBaseline(goldenEntry.id, {
          baseline_summary: goldenEntry.baseline_summary ?? selectedSession.ai_summary ?? '',
          baseline_next_steps: newNextSteps,
        }));
      }
      if (toAdd.length || toRemove.length) {
        calls.push(CuratorService.updateCStar(goldenEntry.id, { add: toAdd, remove: toRemove }));
      }
      await Promise.all(calls);

      const res = await PlaybooksService.getGoldenSetEntries(selectedSession.client_id, { session_id: selectedSession.id });
      setGoldenEntry(res.entries[0] ?? goldenEntry);
      setCurateSuccess(true);
      setTimeout(() => setCurateSuccess(false), 3000);
    } catch (e: any) {
      setCurateError(e.message);
    } finally {
      setCurating(false);
    }
  };

  const clientName = selectedSession
    ? (clients.find((c) => c.id === selectedSession.client_id)?.name ?? selectedSession.client_id.slice(0, 8) + '…')
    : '—';

  const stepFeedbackByIndex = new Map(
    (selectedSession?.step_feedback ?? []).map((fb) => [fb.step_index, fb]),
  );

  return (
    <Box sx={{
      backgroundColor: theme.palette.background.default,
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      px: { xs: 2, sm: 3, md: 4 },
      pt: 6, pb: 3,
    }}>
      <Box sx={{ width: '100%', maxWidth: '100%' }}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 2, sm: 0 },
          mb: 4,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Tooltip title="Back to Playbooks">
              <IconButton
                onClick={() => navigate('/playbooks')}
                size="small"
                sx={{
                  mt: 0.5,
                  color: theme.palette.text.secondary,
                  '&:hover': { color: theme.palette.text.primary },
                }}
              >
                <BackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box>
              <Typography variant="h1" sx={{ color: theme.palette.text.primary, fontWeight: 600, marginBottom: 0.5 }}>
                Review
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                AI-generated response recommendations
              </Typography>
            </Box>
          </Box>

          {selectedSession && (
            <Chip
              label={`${selectedSession.ai_next_steps?.length ?? 0} recommendation${(selectedSession.ai_next_steps?.length ?? 0) !== 1 ? 's' : ''}`}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.65rem',
                fontWeight: 700,
                fontFamily: '"JetBrains Mono", monospace',
                backgroundColor: 'transparent',
                color: theme.palette.text.secondary,
                border: `1px solid ${theme.palette.border?.main ?? theme.palette.divider}`,
                '& .MuiChip-label': { px: 1 },
              }}
            />
          )}
        </Box>

        {/* Session meta */}
        {selectedSession && (
          <Box sx={{ display: 'flex', gap: 4, mb: 4, flexWrap: 'wrap', maxWidth: 800, mx: 'auto' }}>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.tertiary ?? theme.palette.text.disabled,
                  fontWeight: 700,
                  display: 'block',
                  mb: 0.5,
                  letterSpacing: '0.05em',
                }}
              >
                ALERT ID
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  color: theme.palette.primary?.main ?? theme.palette.info.main,
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
                onClick={() => selectedSession.alert_id && navigate(`/alerts?id=${selectedSession.alert_id}`)}
              >
                {(selectedSession.ticket_id || selectedSession.alert_id || '').slice(0, 8)}…
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.tertiary ?? theme.palette.text.disabled,
                  fontWeight: 700,
                  display: 'block',
                  mb: 0.5,
                  letterSpacing: '0.05em',
                }}
              >
                CLIENT
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                {clientName}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.tertiary ?? theme.palette.text.disabled,
                  fontWeight: 700,
                  display: 'block',
                  mb: 0.5,
                  letterSpacing: '0.05em',
                }}
              >
                CREATED
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {getRelativeTimeString(selectedSession.created_at)}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Content */}
        {isDetailsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : !selectedSession ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 8, gap: 1.5 }}>
            <PlaybookIcon sx={{ fontSize: 40, color: theme.palette.text.tertiary ?? theme.palette.text.disabled }} />
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              No playbook generated — no IOCs enriched for this alert
            </Typography>
          </Box>
        ) : selectedSession.incident_type ? (
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <DDoSPlaybookPanel
              session={selectedSession}
              onSessionRefresh={() => { if (id) dispatch(fetchRecommendationsForSession(id) as any); }}
            />
            {recommendations.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="caption" sx={{
                  color: theme.palette.text.tertiary,
                  fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.08em',
                  display: 'block', mb: 1.5,
                }}>
                  IOC RECOMMENDATIONS
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {recommendations.map((rec) => (
                    <RecommendationCard key={rec.id} rec={rec} />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 800, mx: 'auto' }}>
            {selectedSession.ai_summary && (
              <Box sx={{ mb: 1, p: 1.5, borderRadius: 1.5, backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.06) }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                  <LightbulbIcon sx={{ fontSize: 13, color: theme.palette.warning.main }} />
                  <Typography variant="caption" sx={{ color: theme.palette.warning.main, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.08em' }}>
                    AI INCIDENT SUMMARY
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.78rem', lineHeight: 1.6, whiteSpace: 'pre-wrap', display: 'block' }}>
                  {selectedSession.ai_summary}
                </Typography>
              </Box>
            )}
            {recommendations.map((rec) => (
              <RecommendationCard key={rec.id} rec={rec} />
            ))}

            {/* Analyst's own note on this playbook (captured on the ticket page) */}
            {selectedSession.analyst_verdict_notes && (
              <Box sx={{ mb: 1, p: 1.5, borderRadius: 1.5, backgroundColor: colorUtils.withOpacity(theme.palette.text.secondary, 0.06), border: `1px solid ${theme.palette.border.light}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                  <NotesIcon sx={{ fontSize: 13, color: theme.palette.text.secondary }} />
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.08em' }}>
                    ANALYST NOTE
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.78rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', display: 'block' }}>
                  {selectedSession.analyst_verdict_notes}
                </Typography>
              </Box>
            )}

          </Box>
        )}

        {/* AI recommended next steps + retrieved past incidents — shown for every incident
            type (not just IOC-based sessions), since this is the actual golden-set baseline
            material (ai_next_steps / relevant_chunk_ids), independent of the procedural
            playbook checklist DDoSPlaybookPanel shows above for typed incidents. */}
        {selectedSession && !isDetailsLoading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 800, mx: 'auto', mt: selectedSession.incident_type ? 2 : 0 }}>
            {selectedSession.ai_next_steps && selectedSession.ai_next_steps.length > 0 && (
              <Box sx={{ mb: 1, p: 1.5, borderRadius: 1.5, backgroundColor: colorUtils.withOpacity(theme.palette.info.main, 0.05), border: `1px solid ${colorUtils.withOpacity(theme.palette.info.main, 0.12)}` }}>
                <Typography variant="caption" sx={{ color: theme.palette.info.main, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.08em', display: 'block', mb: 1 }}>
                  AI RECOMMENDED ACTIONS — editable
                </Typography>
                {selectedSession.ai_next_steps.map((item, idx) => {
                  const fb = stepFeedbackByIndex.get(idx);
                  const decision = fb?.decision;
                  const statusColor = decision === 'skipped'
                    ? theme.palette.error.main
                    : decision === 'accepted'
                      ? theme.palette.success.main
                      : theme.palette.text.tertiary;
                  const statusIcon = decision === 'skipped'
                    ? <SkippedIcon sx={{ fontSize: 12 }} />
                    : decision === 'accepted'
                      ? <CorrectIcon sx={{ fontSize: 12 }} />
                      : <UnreviewedIcon sx={{ fontSize: 12 }} />;
                  const statusLabel = decision === 'skipped' ? 'NOT ACCEPTED' : decision === 'accepted' ? 'ACCEPTED' : 'NOT REVIEWED';

                  return (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex', gap: 1.25, mb: idx < selectedSession.ai_next_steps!.length - 1 ? 1.5 : 0,
                        p: 1, borderRadius: 1,
                        borderLeft: `3px solid ${decision === 'skipped' ? statusColor : 'transparent'}`,
                        backgroundColor: decision === 'skipped' ? colorUtils.withOpacity(statusColor, 0.06) : 'transparent',
                      }}
                    >
                      <Box sx={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, mt: 0.1, backgroundColor: colorUtils.withOpacity(statusColor, 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: statusColor }}>{idx + 1}</Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                          <Chip
                            icon={statusIcon}
                            label={statusLabel}
                            size="small"
                            sx={{
                              height: 16, fontSize: '0.55rem', fontWeight: 700,
                              color: statusColor,
                              backgroundColor: colorUtils.withOpacity(statusColor, 0.1),
                              '& .MuiChip-icon': { color: statusColor, ml: 0.5, fontSize: 12 },
                            }}
                          />
                        </Box>
                        <TextField
                          value={editedSteps[idx] ?? item.step}
                          onChange={(e) => setEditedSteps((prev) => {
                            const next = [...prev];
                            next[idx] = e.target.value;
                            return next;
                          })}
                          multiline
                          fullWidth
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: '0.78rem',
                              backgroundColor: theme.palette.background.tertiary,
                              '& fieldset': { borderColor: theme.palette.border?.light ?? theme.palette.divider },
                            },
                          }}
                        />
                        {item.rationale && (
                          <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.7rem', lineHeight: 1.4, display: 'block', mt: 0.5 }}>
                            {item.rationale}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* Past incidents used as retrieval context — real MITRE/IOC/enrichment data */}
            {(pastIncidentsLoading || pastIncidents.length > 0) && (
              <Box sx={{ mb: 1, p: 1.5, borderRadius: 1.5, backgroundColor: theme.palette.background.tertiary, border: `1px solid ${theme.palette.border.light}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                  <HistoryIcon sx={{ fontSize: 13, color: theme.palette.text.secondary }} />
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.08em' }}>
                    PAST INCIDENTS USED — mark relevant context
                  </Typography>
                </Box>
                {pastIncidentsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={16} />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {pastIncidents.map((m) => {
                      const meta = m.memory_metadata;
                      const checked = selectedChunkIds.has(m.id);
                      return (
                        <Box
                          key={m.id}
                          onClick={() => toggleChunk(m.id)}
                          sx={{
                            display: 'flex', gap: 1, p: 1.25, borderRadius: 1, cursor: 'pointer',
                            backgroundColor: checked ? colorUtils.withOpacity(theme.palette.success.main, 0.06) : theme.palette.background.paper,
                            border: `1px solid ${checked ? colorUtils.withOpacity(theme.palette.success.main, 0.4) : theme.palette.border.light}`,
                          }}
                        >
                          <Checkbox checked={checked} size="small" sx={{ p: 0, mt: 0.25, flexShrink: 0 }} onClick={(e) => e.stopPropagation()} onChange={() => toggleChunk(m.id)} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
                              {m.incident_type && (
                                <Chip label={m.incident_type.toUpperCase()} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }} />
                              )}
                              {meta?.hostnames?.map((h) => (
                                <Chip key={h} label={h} size="small" sx={{ height: 16, fontSize: '0.58rem', backgroundColor: theme.palette.background.tertiary, color: theme.palette.text.tertiary }} />
                              ))}
                              <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.65rem', ml: 'auto' }}>
                                {getRelativeTimeString(m.created_at)}
                              </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.72rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', display: 'block' }}>
                              {m.content_text}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Golden set curation — available for every incident type, not just IOC-based */}
        {selectedSession && !isDetailsLoading && selectedSession.ai_next_steps && selectedSession.ai_next_steps.length > 0 && (
          <Box sx={{ maxWidth: 800, mx: 'auto', mt: selectedSession.incident_type ? 3 : 0 }}>
            <Box sx={{ mb: 1, p: 1.5, borderRadius: 1.5, backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.05), border: `1px solid ${colorUtils.withOpacity(theme.palette.warning.main, 0.2)}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                <GoldenSetIcon sx={{ fontSize: 14, color: theme.palette.warning.main }} />
                <Typography variant="caption" sx={{ color: theme.palette.warning.main, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.08em' }}>
                  GOLDEN SET
                </Typography>
              </Box>

              {goldenEntryLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <CircularProgress size={14} />
                </Box>
              ) : goldenEntry ? (
                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                    {goldenEntry.name || 'Untitled entry'} — quality {goldenEntry.quality_score != null ? `${Math.round(goldenEntry.quality_score * 100)}%` : '—'}, {(goldenEntry.relevant_chunk_ids ?? []).length} relevant incidents
                  </Typography>
                </Box>
              ) : (
                <TextField
                  placeholder="Notes for this golden set entry (optional)"
                  value={goldenNotes}
                  onChange={(e) => setGoldenNotes(e.target.value)}
                  multiline
                  minRows={2}
                  size="small"
                  fullWidth
                  sx={{
                    mb: 1.5,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme.palette.background.paper,
                      fontSize: '0.82rem',
                      '& fieldset': { borderColor: theme.palette.border.main },
                    },
                  }}
                />
              )}

              {curateError && (
                <Typography variant="caption" sx={{ color: theme.palette.error.main, display: 'block', mb: 1 }}>{curateError}</Typography>
              )}

              <Button
                variant="contained"
                size="small"
                disabled={curating || goldenEntryLoading}
                onClick={goldenEntry ? handleCurate : handleAddToGoldenSet}
                startIcon={curating ? <CircularProgress size={13} color="inherit" /> : curateSuccess ? <CorrectIcon sx={{ fontSize: 14 }} /> : <GoldenSetIcon sx={{ fontSize: 14 }} />}
                sx={{
                  fontWeight: 600, px: 2,
                  backgroundColor: theme.palette.warning.main,
                  '&:hover': { backgroundColor: theme.palette.warning.dark },
                }}
              >
                {curating ? 'Saving…' : curateSuccess ? 'Saved' : goldenEntry ? 'Curate' : 'Add to Golden Set'}
              </Button>
            </Box>
          </Box>
        )}

        {/* ── Analyst Verdict ─────────────────────────────────────── */}
        {selectedSession && !isDetailsLoading && (
          <Box sx={{ mt: 4, maxWidth: 800, mx: 'auto', pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <VerdictIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
              <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, letterSpacing: '0.05em', fontSize: '0.65rem', textTransform: 'uppercase' }}>
                Analyst Verdict
              </Typography>
              {verdictSaved && selectedSession.analyst_verdict && (
                <Chip
                  label={selectedSession.analyst_verdict.toUpperCase()}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    color: selectedSession.analyst_verdict === 'correct' ? theme.palette.success.main : selectedSession.analyst_verdict === 'incorrect' ? theme.palette.error.main : theme.palette.warning.main,
                    backgroundColor: colorUtils.withOpacity(selectedSession.analyst_verdict === 'correct' ? theme.palette.success.main : selectedSession.analyst_verdict === 'incorrect' ? theme.palette.error.main : theme.palette.warning.main, 0.1),
                  }}
                />
              )}
            </Box>
            <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, display: 'block', mb: 2, fontSize: '0.72rem' }}>
              Was the AI-generated playbook correct overall? This macro-verdict feeds aggregate accuracy metrics.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
              {([
                { v: 'correct' as const,   label: 'Correct',   color: theme.palette.success.main,  icon: <CorrectIcon   sx={{ fontSize: 16 }} /> },
                { v: 'incorrect' as const, label: 'Incorrect', color: theme.palette.error.main,    icon: <IncorrectIcon sx={{ fontSize: 16 }} /> },
                { v: 'partial' as const,   label: 'Partial',   color: theme.palette.warning.main,  icon: <PartialIcon   sx={{ fontSize: 16 }} /> },
              ]).map(({ v, label, color, icon }) => {
                const selected = verdictSelected === v;
                return (
                  <Box
                    key={v}
                    onClick={() => { setVerdictSelected(v); setVerdictSaved(false); }}
                    sx={{
                      px: 2, py: 1, borderRadius: 1.5, cursor: 'pointer',
                      border: `2px solid ${selected ? color : theme.palette.border.main}`,
                      backgroundColor: selected ? colorUtils.withOpacity(color, 0.08) : 'transparent',
                      display: 'flex', alignItems: 'center', gap: 0.75,
                      transition: 'all 0.15s ease',
                      '&:hover': { borderColor: color, backgroundColor: colorUtils.withOpacity(color, 0.05) },
                    }}
                  >
                    <Box sx={{ color: selected ? color : theme.palette.text.tertiary }}>{icon}</Box>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: selected ? 700 : 500, color: selected ? color : theme.palette.text.secondary }}>{label}</Typography>
                  </Box>
                );
              })}
            </Box>

            <TextField
              placeholder="Notes (optional) — what was correct or incorrect?"
              value={verdictNotes}
              onChange={(e) => { setVerdictNotes(e.target.value); setVerdictSaved(false); }}
              multiline
              minRows={2}
              size="small"
              fullWidth
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.palette.background.paper,
                  fontSize: '0.82rem',
                  '& fieldset': { borderColor: theme.palette.border.main },
                },
              }}
            />

            {verdictError && (
              <Typography variant="caption" sx={{ color: theme.palette.error.main, display: 'block', mb: 1 }}>{verdictError}</Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button
                variant="contained"
                size="small"
                disabled={!verdictSelected || verdictSubmitting || verdictSaved}
                onClick={handleSubmitVerdict}
                startIcon={verdictSubmitting ? <CircularProgress size={13} color="inherit" /> : verdictSaved ? <CorrectIcon sx={{ fontSize: 14 }} /> : <VerdictIcon sx={{ fontSize: 14 }} />}
                sx={{
                  fontWeight: 600,
                  px: 2,
                  backgroundColor: verdictSaved ? theme.palette.success.main : undefined,
                  '&:hover': { backgroundColor: verdictSaved ? theme.palette.success.dark : undefined },
                }}
              >
                {verdictSubmitting ? 'Submitting…' : verdictSaved ? 'Verdict submitted' : 'Submit verdict'}
              </Button>
              {verdictSaved && selectedSession.analyst_verdict_at && (
                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                  Recorded {new Date(selectedSession.analyst_verdict_at).toLocaleString()}
                </Typography>
              )}
            </Box>
          </Box>
        )}

      </Box>
    </Box>
  );
};

export default SessionDetailPage;
